import { isLocalHost } from "./is-local-host";

type Logger = {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
};

type LevelName = keyof Logger;

// biome-ignore lint/suspicious/noEmptyBlockStatements: <documentation requirement>
const noop = () => {};

const isDev = isLocalHost();

/**
 * Логгер живёт только на localhost, в проде все методы — no-op.
 * Раньше форматированием занимался pino: ~15 KB исходника ехали в каждый прод-бандл, хотя
 * ни одна строка из них там не исполнялась (браузерный транспорт pino всё равно писал в console).
 * Формат вывода сохранён один в один — меняется только то, чем он собирается.
 */
const LEVEL_STYLES: Record<LevelName, { label: string; color: string; icon: string; method: (...args: unknown[]) => void }> = {
  debug: { label: "DEBUG", color: "#8b5cf6", icon: "🐛", method: console.debug },
  info: { label: "INFO", color: "#3b82f6", icon: "ℹ️", method: console.info },
  warn: { label: "WARN", color: "#f59e0b", icon: "⚠️", method: console.warn },
  error: { label: "ERROR", color: "#ef4444", icon: "🚨", method: console.error },
};

const formatTime = (): string =>
  new Date().toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
  });

const createLevelLogger =
  (level: LevelName) =>
  (...args: unknown[]): void => {
    const { label, color, icon, method } = LEVEL_STYLES[level];
    const [first, ...rest] = args;

    if (typeof first !== "string") {
      method(...args);
      return;
    }

    const badgeStyle = `background: ${color}; color: white; padding: 2px 6px; border-radius: 4px; font-weight: bold; font-size: 10px;`;
    const timeStyle = "color: #9ca3af; font-size: 10px; font-weight: normal; margin-left: 6px;";
    const msgStyle = "font-weight: 600; color: inherit; margin-left: 6px;";

    method(`%c${icon} ${label}%c${formatTime()}%c${first}`, badgeStyle, timeStyle, msgStyle, ...rest);
  };

const logger: Logger = isDev
  ? { debug: createLevelLogger("debug"), info: createLevelLogger("info"), warn: createLevelLogger("warn"), error: createLevelLogger("error") }
  : { debug: noop, info: noop, warn: noop, error: noop };

export default logger;
